<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12">
        <v-card class="elevation-2 rounded-lg">
          <v-card-title class="headline primary white--text">
            Profile
          </v-card-title>

          <v-card-text class="pt-4">
            <v-form ref="form" v-model="valid">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="name"
                    :rules="nameRules"
                    label="Name"
                    prepend-icon="mdi-account"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="email"
                    :rules="emailRules"
                    label="Current email"
                    readonly
                    prepend-icon="mdi-email"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
              </v-row>

              <section v-if="currentUser && !currentUser.is_anonymous && currentUser.email_verified" class="mb-4">
                <h2 class="text-subtitle-1">Change email address</h2>
                <p>Your current address stays active until you confirm the new one.</p>
                <v-alert v-if="currentUser.pending_email" type="info">
                  Awaiting confirmation: {{ currentUser.pending_email }}. Expires {{ currentUser.pending_email_expires_at }}.
                </v-alert>
                <v-text-field v-model="newEmail" label="New email" type="email" outlined dense />
                <v-text-field v-model="currentPassword" label="Current password" type="password" autocomplete="current-password" outlined dense />
                <v-btn color="primary" :disabled="!newEmail || !currentPassword || emailBusy" :loading="emailBusy" @click="requestEmailChange">
                  {{ currentUser.pending_email ? "Resend confirmation" : "Send confirmation" }}
                </v-btn>
                <v-btn v-if="currentUser.pending_email" text :disabled="emailBusy" @click="cancelEmailChange">Cancel pending change</v-btn>
                <v-alert v-if="emailMessage" :type="emailFailed ? 'error' : 'info'" class="mt-3">{{ emailMessage }}</v-alert>
              </section>

              <v-divider class="my-4"></v-divider>

              <v-row>
                <v-col cols="12" :md="creditsEnabled ? 6 : 12">
                  <v-card
                    outlined
                    class="pa-3 subscription-card"
                    hover
                    to="/subscription"
                  >
                    <div
                      class="
                        text-subtitle-1
                        font-weight-medium
                        mb-2
                        d-flex
                        align-center
                      "
                    >
                      Subscription
                      <v-icon small class="ml-1">mdi-arrow-right</v-icon>
                    </div>
                    <div class="d-flex align-center">
                      <v-icon color="primary" class="mr-2"
                        >mdi-card-account-details</v-icon
                      >
                      <span>{{ subscription }}</span>
                    </div>
                  </v-card>
                </v-col>

                <!-- Apps without credits (CREDITS_ENABLED false) have no balance to show. -->
                <v-col v-if="creditsEnabled" cols="12" md="6">
                  <v-card
                    outlined
                    class="pa-3 credits-card"
                    hover
                    to="/billing"
                  >
                    <div
                      class="
                        text-subtitle-1
                        font-weight-medium
                        mb-2
                        d-flex
                        align-center
                      "
                    >
                      Credits
                      <v-icon small class="ml-1">mdi-arrow-right</v-icon>
                    </div>
                    <div class="d-flex align-center">
                      <v-icon color="primary" class="mr-2"
                        >mdi-currency-usd</v-icon
                      >
                      <span>{{ credits }}</span>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <!-- API Key section hidden - no user-facing API currently available -->
              <!-- To re-enable: change v-if="false" to v-if="true" -->
              <v-card v-if="false" outlined class="pa-3 mb-4">
                <div class="text-subtitle-1 font-weight-medium mb-2">
                  API Key
                </div>
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="apiKey"
                    readonly
                    append-icon="mdi-content-copy"
                    @click:append="copyApiKey"
                    hide-details
                    class="mb-0"
                  ></v-text-field>
                </div>
                <div class="text-caption mt-2">
                  This API key provides access to your account. Keep it secure
                  and do not share it.
                </div>
              </v-card>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :disabled="!valid || !isChanged || loading"
              :loading="loading"
              @click="updateProfile"
            >
              Save Changes
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Success Alert -->
        <v-alert
          v-if="successMessage"
          type="success"
          dismissible
          class="mt-4"
          @input="successMessage = ''"
        >
          {{ successMessage }}
        </v-alert>

        <!-- Error Alert -->
        <v-alert
          v-if="error"
          type="error"
          dismissible
          class="mt-4"
          @input="clearError"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { apiClient } from "@shared/services/api";
import { mapGetters } from "vuex";
import { formatPlanName } from "@shared/utils/planLabels";
import { creditsEnabled } from "@shared/utils/productWording";

export default {
  name: "ProfileView",
  data() {
    return {
      newEmail: "",
      currentPassword: "",
      emailBusy: false,
      emailMessage: "",
      emailFailed: false,
      valid: true,
      name: "",
      email: "",
      apiKey: "",
      originalName: "",
      originalEmail: "",
      successMessage: "",
      nameRules: [
        v => !!v || "Name is required",
        v => v.length >= 2 || "Name must be at least 2 characters"
      ],
      emailRules: [
        v => !!v || "Email is required",
        v => /.+@.+\..+/.test(v) || "Email must be valid"
      ]
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "loading",
      "error",
      "currentSubscription",
      "creditBalance"
    ]),
    isChanged() {
      return (
        this.name !== this.originalName
      );
    },
    creditsEnabled() {
      return creditsEnabled();
    },
    subscription() {
      return this.formatPlanName(
        this.currentSubscription && this.currentSubscription.subscription_plan
      );
    },
    credits() {
      return (this.creditBalance && this.creditBalance.balance) || 0;
    }
  },
  created() {
    this.loadUserData();
  },
  methods: {
    async requestEmailChange() {
      this.emailBusy = true;
      this.emailMessage = "";
      try {
        const response = await apiClient.post("/users/me/email-change", { new_email: this.newEmail, current_password: this.currentPassword });
        this.emailMessage = response.data.detail;
        this.emailFailed = response.data.delivery === "failed";
        await this.$store.dispatch("fetchCurrentUser");
      } catch (err) {
        const detail = err.response && err.response.data && err.response.data.detail;
        this.emailFailed = true;
        this.emailMessage = typeof detail === "string" ? detail : "Could not request the email change.";
      } finally { this.emailBusy = false; this.currentPassword = ""; }
    },
    async cancelEmailChange() {
      this.emailBusy = true;
      try {
        await apiClient.delete("/users/me/email-change");
        await this.$store.dispatch("fetchCurrentUser");
        this.emailFailed = false;
        this.emailMessage = "Pending email change cancelled.";
      } catch (err) { this.emailFailed = true; this.emailMessage = "Could not cancel. Please retry."; }
      finally { this.emailBusy = false; }
    },
    formatPlanName,
    loadUserData() {
      if (this.currentUser) {
        this.name = this.currentUser.name;
        this.email = this.currentUser.email;
        this.newEmail = this.currentUser.pending_email || "";
        this.apiKey = this.currentUser.api_key;
        this.originalName = this.currentUser.name;
        this.originalEmail = this.currentUser.email;
      } else {
        // If user data is not in the store, fetch it
        this.$store.dispatch("fetchCurrentUser").then(() => {
          this.name = this.currentUser.name;
          this.email = this.currentUser.email;
        this.newEmail = this.currentUser.pending_email || "";
          this.apiKey = this.currentUser.api_key;
          this.originalName = this.currentUser.name;
          this.originalEmail = this.currentUser.email;
        });
      }
    },
    async updateProfile() {
      if (this.$refs.form.validate()) {
        try {
          await this.$store.dispatch("updateProfile", {
            name: this.name
          });

          // Update original values after successful update
          this.originalName = this.name;
          this.originalEmail = this.email;

          this.successMessage = "Profile updated successfully";
        } catch (error) {
          // Error is handled by the store
          console.error("Profile update failed:", error);
        }
      }
    },
    copyApiKey() {
      navigator.clipboard.writeText(this.apiKey).then(() => {
        this.successMessage = "API key copied to clipboard";
      });
    },
    clearError() {
      this.$store.commit("SET_ERROR", null);
    }
  }
};
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.subscription-card,
.credits-card {
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  color: inherit;
}

.subscription-card:hover,
.credits-card:hover {
  background-color: rgba(var(--v-primary-base), 0.05);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px var(--shadow-xs);
}
</style>
